import {
  CanonicalErrorCode,
  featureFlags,
  Kaggle,
  kernelsClient,
} from "@kaggle/sdk";
import { DataSourceType } from "@kaggle/sdk/datasets/databundles";
import { fetchKernel } from "../../../api/kernel/fetchKernel";
import { getFirestoreClient } from "../../redux/actions/firebase";
import { getKernelVersions } from "../../session/apiClient";
import { getSessionInfo } from "../../session/getSessionInfo";
import { JupyterSource } from "../../session/JupyterSource";
import { validateDraft } from "../../session/validateDraft";
import validateKernel from "../../session/validateKernel";
import { SetDraft } from "../../types/DraftSpec";
import { SetKernel } from "../../types/KernelSpec";
import { editorErrorOccurred, userErrorOccurred } from "../actions/editor";
import {
  fetchOutputDataset,
  fetchTags,
  setCanToggleVisibility,
  setCompetitionsPaneInfo,
  setDataSources,
  setDraftSpec,
  setKernelSpec,
  setRun,
} from "../actions/notebook";
import { checkWorkspaceUpdates } from "../actions/notebook/checkWorkspaceUpdates";
import { getDockerImages } from "../actions/notebook/docker";
import { setSessionTimeoutSeconds } from "../actions/timer";
import {
  ensureSupportedLanguageName,
  getLanguageBySourceTypeAndName,
} from "../selectors/kernelDraft";
import { fetchDefaultSessionSettings } from "./defaultSessionSettings";
import {
  showAcceleratorQuotaExceededModal,
  showMaxSesionsExceededModal,
} from "./modal";
import { fetchCollaborators } from "./notebook/collaborators";
import { fetchSchedule } from "./notebook/schedule";

const maxSessionsRegex = new RegExp(
  "Maximum ([\\w]+) ([\\w]+) session count of ([0-9]+) reached."
);
const maxQuotaRegex = new RegExp(
  "Maximum weekly ([\\w]+) quota of ([\\d\\.]+) hours reached."
);
const quotaRegex = [
  maxSessionsRegex,
  maxQuotaRegex,
  new RegExp(
    "Session cannot start because currently running sessions are projected to exceed maximum weekly [\\w]+ quota of ([\\d]+)."
  ),
];

export function handleQuotaError(message: string) {
  const matchMaxSessions = message.match(maxSessionsRegex);
  if (matchMaxSessions) {
    const executionTypeLabel = matchMaxSessions[1];
    const acceleratorLabel = matchMaxSessions[2];
    const hours = parseInt(matchMaxSessions[3], 10);
    showMaxSesionsExceededModal(executionTypeLabel, acceleratorLabel, hours);
    return true;
  }
  const matchMaxQuota = message.match(maxQuotaRegex);
  if (matchMaxQuota) {
    const acceleratorLabel = matchMaxQuota[1];
    const accelerator = getAcceleratorForLabel(acceleratorLabel);
    showAcceleratorQuotaExceededModal(accelerator, acceleratorLabel);
    return true;
  }
  return false;
}

export function isQuotaError(message: string) {
  if (!message) {
    return false;
  }

  return quotaRegex.some(re => message.match(re));
}

export function getAcceleratorForLabel(
  acceleratorLabel: string
): Kaggle.Sdk.Kernels.AcceleratorType {
  switch (acceleratorLabel.toLowerCase()) {
    case "gpu":
      return Kaggle.Sdk.Kernels.AcceleratorType.GPU;
    case "tpu":
      return Kaggle.Sdk.Kernels.AcceleratorType.TPU_V3_8;
    default:
      return Kaggle.Sdk.Kernels.AcceleratorType.NONE;
  }
}

// TODO(cochamberlain): This is eager transformation. Lets get rid of it.
const setDraft: SetDraft = (kernelId, maybeDraft) => {
  if (!kernelId) {
    throw new Error("Cannot set draft with no kernelId.");
  }
  const draft = validateDraft(maybeDraft);
  if (!draft.blob.source) {
    throw new Error(
      "Cannot create JupyterSource from non-existent draft source."
    );
  }
  const source = new JupyterSource(draft.blob.source);
  const sourceType = draft.blob.settings.sourceType;
  const languageName = ensureSupportedLanguageName(
    draft.blob.settings.language
  );
  const language = getLanguageBySourceTypeAndName(sourceType, languageName);
  if (language === null) {
    throw new Error(
      `No language could be found for languageName ${languageName} on hydration.`
    );
  }
  const languageId = language.id;
  const spec = {
    sequence: draft.sequence,
    source,
    languageId,
    internet: draft.blob.settings.isInternetEnabled,
    accelerator: draft.blob.settings.accelerator,
  };
  // Sets the draft atomically across reducers.
  setDraftSpec(kernelId, draft, spec);
  fetchTags();
  fetchCollaborators();
  fetchDefaultSessionSettings();
  fetchSchedule();
  fetchOutputDataset();

  // Load kernel versions. Used to initialize the versions entities
  // so we know whether or not to show the banner indicating that the user
  // needs to commit kernel.
  getKernelVersions();

  return spec;
};

const setKernel: SetKernel = maybeKernel => {
  const kernel = validateKernel(maybeKernel);

  const spec = {
    id: kernel.id,
    title: kernel.title ?? "",
    disableComments: kernel.disableComments,
    isPrivate: kernel.isPrivate,
    canToggleVisibility: kernel.canToggleVisibility,
    slug: kernel.slug ?? "",
    readGroup: kernel.readGroupId ?? null,
    writeGroup: kernel.writeGroupId ?? null,
    dockerImageTag: "",
    workerPoolName: "",
    // Current kernels url. i.e: "willbrazil/mykernel123"
    kernelIdPath: kernel.url ?? "",
    authorUsername: kernel.author.userName!,
    pinnedDockerImage: kernel.pinnedDockerImageVersionId ?? null,
    dockerImagePinningType: kernel.dockerImagePinningType,
  };
  setKernelSpec(spec);
  return spec;
};

export const loadKernel = async (
  id?: number,
  username?: string,
  slug?: string
) => {
  try {
    const kernel = await fetchKernel(id, username, slug);

    const kernelSpec = setKernel(kernel);
    const { runId, sessionTimeoutSeconds, isCanceled } = await getSessionInfo(
      kernelSpec.id,
      setDraft
    );

    setCanToggleVisibility(kernel.canToggleVisibility);
    setRun(runId);
    if (!isCanceled) {
      setSessionTimeoutSeconds(sessionTimeoutSeconds);

      // Error thrown by firebase like "This operation is restricted to admins"
      // will appear as an editor error.
      const firestoreClient = await getFirestoreClient();
      await firestoreClient.setAllFirestoreListeners(runId);
    }

    const notebookResponse =
      await kernelsClient.kernelsService.getKernelSessionDataSources({
        kernelSessionId: runId,
        includeOutputFiles: false,
        hierarchicalOutputFiles: false,
      });

    setDataSources(
      notebookResponse.dataSources.map(s => ({ ...s, refreshAvailable: false }))
    );
    if (featureFlags.datasetsDataExplorerV3CheckForUpdatesInBackground) {
      window.setInterval(() => {
        checkWorkspaceUpdates();
      }, 2 * 60 * 1000);
    }

    const competitionDataSources = notebookResponse.dataSources.filter(
      s => s.sourceType === DataSourceType.COMPETITION
    );
    if (
      competitionDataSources.length === 1 &&
      competitionDataSources[0].databundle.competitionInfo
    ) {
      try {
        const competitionsPaneInfo =
          await kernelsClient.kernelsService.getCompetitionsPaneInfo({
            kernelId: kernel.id,
            competitionId: competitionDataSources[0].sourceId,
          });
        setCompetitionsPaneInfo(competitionsPaneInfo);
      } catch (error) {
        if (error?.code === CanonicalErrorCode.UNAUTHENTICATED) {
          setCompetitionsPaneInfo(null);
        } else {
          throw error;
        }
      }
    }

    // Load Docker images used in the Docker environment selector modal.
    getDockerImages(runId);
  } catch (error) {
    if (isQuotaError(error.message)) {
      userErrorOccurred(error.message);
    } else {
      editorErrorOccurred(error.message);
    }
  }
};
